import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import Container from '@/components/generals/container'
import AppImage from '@/components/generals/app-image'
import SectionHeading from '@/components/generals/section-heading'
import { customizationOptions } from '@/constants/content'
import { quoteHref, sectionImages } from '@/constants/site'

const CustomizationSection: FC = (): ReactNode => {
    return (
        <section id='personalizacion' aria-labelledby='personalizacion-titulo' className='bg-black py-24 md:py-32'>
            <Container className='grid items-center gap-16 lg:grid-cols-12 lg:gap-20'>
                <div className='reveal relative mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none'>
                    <div aria-hidden='true' className='absolute -top-4 -left-4 h-full w-full rounded-sm border border-brass/30' />

                    <div className='relative aspect-[4/5] overflow-hidden rounded-sm bg-graphite'>
                        <AppImage
                            src={sectionImages.personalizacion}
                            alt='Grabado láser de una orla de laurel sobre una placa de latón cepillado'
                            sizes='(min-width: 1024px) 40vw, 90vw'
                        />
                    </div>

                    <div className='absolute -right-2 -bottom-6 max-w-[15rem] rounded-sm border border-ivory/10 bg-graphite/95 p-5 shadow-2xl shadow-black/60 sm:-right-6'>
                        <p className='text-[0.62rem] font-semibold uppercase tracking-[0.26em] text-brass'>Grabado de precisión</p>
                        <p className='mt-2 text-sm leading-relaxed text-ivory/80'>Cada texto se revisa contigo antes de pasar a producción.</p>
                    </div>
                </div>

                <div className='flex flex-col gap-12 lg:col-span-7'>
                    <SectionHeading
                        id='personalizacion-titulo'
                        eyebrow='Personalización'
                        title='Cada logro es único. Su reconocimiento también.'
                        description='Diseñamos cada pieza a partir de lo que deseas expresar: quién recibe el reconocimiento, qué se celebra y dónde se exhibirá.'
                    />

                    <ul className='grid gap-x-10 gap-y-8 sm:grid-cols-2'>
                        {customizationOptions.map(option => {
                            const Icon = option.icon

                            return (
                                <li key={option.title} className='flex gap-4'>
                                    <span className='inline-flex size-11 shrink-0 items-center justify-center rounded-sm border border-brass/35 text-brass'>
                                        <Icon aria-hidden='true' className='size-5' />
                                    </span>

                                    <div className='flex flex-col gap-1.5'>
                                        <h3 className='font-heading text-xl leading-tight font-medium text-foreground'>{option.title}</h3>
                                        <p className='text-sm leading-relaxed text-muted-foreground'>{option.description}</p>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='flex flex-col gap-4 sm:flex-row'>
                        <Link href={quoteHref} className={buttonVariants()}>
                            Solicitar cotización
                            <ArrowRight aria-hidden='true' />
                        </Link>

                        <Link href='/servicios' className={buttonVariants({ variant: 'outline' })}>
                            Conocer servicios
                        </Link>
                    </div>
                </div>
            </Container>
        </section>
    )
}

export default CustomizationSection
